import { useCallback } from 'react';

type ScrollToFieldOptions = {
  behavior?: ScrollBehavior;
  block?: ScrollLogicalPosition;
};

/**
 * A reusable hook for handling scroll-to-field functionality
 *
 * @param fieldRefMap - A partial record mapping field identifiers to their corresponding refs;
 *   callers may parameterize with a wider key union and supply refs for only a subset of fields
 * @param options - Optional scroll behavior configuration
 * @returns A function that scrolls to a specific field if it's not in viewport
 */
export function useScrollToField<TFieldName extends string>(
  fieldRefMap: Partial<Record<TFieldName, React.RefObject<HTMLElement | null>>>,
  options: ScrollToFieldOptions = {},
) {
  const { behavior = 'smooth', block = 'center' } = options;

  // Check if element is visible in viewport
  const isElementInViewport = useCallback((element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }, []);

  // Scroll to field function
  const scrollToField = useCallback(
    (fieldName: TFieldName) => {
      const targetRef = fieldRefMap[fieldName];
      const element = targetRef?.current;

      if (!element) return;

      // Only scroll if the element is not in viewport
      if (!isElementInViewport(element)) {
        const scrollOptions: ScrollIntoViewOptions = {
          behavior,
          block,
        };
        element.scrollIntoView(scrollOptions);
      }
    },
    [fieldRefMap, isElementInViewport, behavior, block],
  );

  return scrollToField;
}

/**
 * A utility function for creating simple scroll-to-element functionality
 * Useful for cases with dynamic refs or simple scrolling needs
 *
 * @param options - Optional scroll behavior configuration
 * @returns Object with isElementInViewport helper and scrollToElement function
 */
export function useScrollToElement(options: ScrollToFieldOptions = {}) {
  const { behavior = 'smooth', block = 'center' } = options;

  // Check if element is visible in viewport
  const isElementInViewport = useCallback((element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }, []);

  // Scroll to element function
  const scrollToElement = useCallback(
    (element: HTMLElement | null) => {
      if (!element) return;

      // Only scroll if the element is not in viewport
      if (!isElementInViewport(element)) {
        const scrollOptions: ScrollIntoViewOptions = {
          behavior,
          block,
        };
        element.scrollIntoView(scrollOptions);
      }
    },
    [isElementInViewport, behavior, block],
  );

  return { isElementInViewport, scrollToElement };
}
